import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Avatar, { AvatarSize } from 'components/avatar/Avatar';

interface MenuItem {
  id: string;
  title: string;
  color: string;
  letter: string;
  active: boolean;
  visible: boolean;
  path: string;
  icon: React.ReactNode;
}

interface SidebarProps {
  menuItems: MenuItem[];
  activeMenuItem: string;
  setActiveMenuItem: (menuItem: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  menuItems,
  activeMenuItem, 
}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsCollapsed(window.innerWidth <= 900);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigation = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  // Sadece görünür olanları filtreliyoruz
  const visibleMenuItems = React.useMemo(() => 
    menuItems.filter((item) => item.visible),
    [menuItems]
  );

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <SidebarNav>
        {visibleMenuItems.map((item) => (
          <SidebarNavItem
            key={item.id}
            onClick={() => handleNavigation(item.path)}
            active={activeMenuItem === item.id}
            isCollapsed={isCollapsed}
            data-title={item.title}
          >
            <IconContainer isCollapsed={isCollapsed}>
              <Avatar text={item.title.charAt(0)} size={AvatarSize.average} color={item.color} />
            </IconContainer>

            {!isCollapsed && <ItemTitle>{item.title}</ItemTitle>}
            <ArrowIcon active={activeMenuItem === item.id} isCollapsed={isCollapsed}>›</ArrowIcon>
          </SidebarNavItem>
        ))}
      </SidebarNav>
    </SidebarContainer>
  )
};

export default React.memo(Sidebar);

const SidebarContainer = styled.div<{isCollapsed: boolean}>`
  width: ${props => props.isCollapsed ? '80px' : '17%'};
  min-width: ${props => props.isCollapsed ? '80px' : '200px'};
  max-width: ${props => props.isCollapsed ? '80px' : '300px'};
  background-color: #ffffff00;
  color: #333;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  border-right: 1px solid #dee2e6;
  padding: 0 ${props => props.isCollapsed ? '8px' : '12px'} 0 ${props => props.isCollapsed ? '8px' : '12px'};
  transition: all 0.3s ease;
  
  @media (max-width: 1200px) {
    width: ${props => props.isCollapsed ? '80px' : '20%'};
  }
  
  @media (max-width: 992px) {
    width: ${props => props.isCollapsed ? '80px' : '25%'};
  }
  
  @media (max-width: 768px) {
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    padding: 0 8px;
  }
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0 8px 0;
`;

const SidebarNavItem = styled.div<{active: boolean; isCollapsed: boolean}>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.isCollapsed ? 'center' : 'flex-start'};
  padding: ${props => props.isCollapsed ? '10px 8px' : '10px 16px'};
  font-size: 14px;
  cursor: pointer;
  background-color: ${props => props.active ? '#1f4ab7' : '#FFFFFF'};
  color: ${props => props.active ? '#FFFFFF' : '#333'};
  margin: 8px ${props => props.isCollapsed ? '4px' : '8px'};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #f5f5f5;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    ${props => !props.active && `
      background-color: #f8f9fa;
      transform: translateY(-2px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
    `}
    
    ${props => props.isCollapsed && `
      &::after {
        content: attr(data-title);
        position: absolute;
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        background-color: #333;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        white-space: nowrap;
        font-size: 12px;
        z-index: 1000;
        margin-left: 8px;
        opacity: 0;
        animation: fadeIn 0.2s ease forwards;
      }
      
      @keyframes fadeIn {
        to {
          opacity: 1;
        }
      }
    `}
  }
  
  @media (max-width: 992px) {
    padding: ${props => props.isCollapsed ? '8px 6px' : '8px 12px'};
  }
  
  @media (max-width: 768px) {
    padding: 8px 6px;
    justify-content: center;
    margin: 6px 4px;
  }
`;

const IconContainer = styled.div<{isCollapsed?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.isCollapsed ? '0' : '12px'};
`;

const ItemTitle = styled.span`
  flex: 1;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowIcon = styled.span<{active: boolean; isCollapsed: boolean}>`
  color: ${props => props.active ? '#FFFFFF' : '#333'};
  display: ${props => props.isCollapsed ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  font-size: 25px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;